import { Exercise } from '../database';

// Exercise library version for tracking updates
export const EXERCISE_LIBRARY_VERSION = '1.1.0'; // Updated: Added 9 new exercises

// Generate deterministic ID from exercise name for consistent cross-user IDs
function generateExerciseId(name: string): number {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    const char = name.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  // Ensure positive number and avoid conflicts with auto-generated IDs
  return Math.abs(hash) + 10000;
}

// Exercise library without IDs (for easier maintenance)
const exerciseLibraryRaw: Omit<Exercise, 'id' | 'createdAt' | 'updatedAt'>[] = [
  // Push Exercises
  {
    name: "Bench Press",
    description: "Lie on bench, press barbell from chest to full arm extension",
    category: "Push",
    primaryMuscles: ["Chest"],
    secondaryMuscles: ["Triceps", "Front Delts"]
  },
  {
    name: "Incline Bench Press",
    description: "Bench press on inclined bench, targets upper chest",
    category: "Push",
    primaryMuscles: ["Chest"],
    secondaryMuscles: ["Front Delts", "Triceps"]
  },
  {
    name: "Decline Bench Press", 
    description: "Bench press on declined bench, targets lower chest",
    category: "Push",
    primaryMuscles: ["Chest"],
    secondaryMuscles: ["Triceps", "Front Delts"]
  },
  {
    name: "Dumbbell Bench Press",
    description: "Bench press using dumbbells for greater range of motion",
    category: "Push",
    primaryMuscles: ["Chest"],
    secondaryMuscles: ["Triceps", "Front Delts"]
  },
  {
    name: "Incline Dumbbell Press",
    description: "Incline press with dumbbells, targets upper chest",
    category: "Push",
    primaryMuscles: ["Chest"],
    secondaryMuscles: ["Front Delts", "Triceps"]
  },
  {
    name: "Overhead Press",
    description: "Standing barbell press from shoulders overhead",
    category: "Push",
    primaryMuscles: ["Shoulders"],
    secondaryMuscles: ["Triceps", "Core"]
  },
  {
    name: "Seated Shoulder Press",
    description: "Seated dumbbell or barbell shoulder press",
    category: "Push",
    primaryMuscles: ["Shoulders"],
    secondaryMuscles: ["Triceps"]
  },
  {
    name: "Push-ups",
    description: "Bodyweight chest and tricep exercise",
    category: "Push",
    primaryMuscles: ["Chest"],
    secondaryMuscles: ["Triceps", "Front Delts", "Core"]
  },
  {
    name: "Dips",
    description: "Bodyweight exercise on parallel bars or bench",
    category: "Push",
    primaryMuscles: ["Chest", "Triceps"],
    secondaryMuscles: ["Front Delts"]
  },
  {
    name: "Close-Grip Bench Press",
    description: "Narrow grip bench press targeting triceps",
    category: "Push",
    primaryMuscles: ["Triceps"],
    secondaryMuscles: ["Chest", "Front Delts"]
  },
  {
    name: "Tricep Dips",
    description: "Dips focusing on tricep isolation",
    category: "Push",
    primaryMuscles: ["Triceps"],
    secondaryMuscles: ["Chest", "Front Delts"]
  },
  {
    name: "Overhead Tricep Extension",
    description: "Dumbbell extension behind head for triceps",
    category: "Push",
    primaryMuscles: ["Triceps"],
    secondaryMuscles: ["Core"]
  },
  {
    name: "Lateral Raises",
    description: "Dumbbell side raises for shoulder width",
    category: "Push",
    primaryMuscles: ["Side Delts"],
    secondaryMuscles: ["Traps"]
  },
  {
    name: "Front Raises",
    description: "Dumbbell front raises for anterior deltoids",
    category: "Push",
    primaryMuscles: ["Front Delts"],
    secondaryMuscles: ["Core"]
  },
  {
    name: "Arnold Press",
    description: "Rotating dumbbell shoulder press",
    category: "Push",
    primaryMuscles: ["Shoulders"],
    secondaryMuscles: ["Triceps"]
  },
  {
    name: "Dumbbell Shoulder Press",
    description: "Standing dumbbell shoulder press",
    category: "Push",
    primaryMuscles: ["Shoulders"],
    secondaryMuscles: ["Triceps", "Core"]
  },
  {
    name: "Decline Dumbbell Press",
    description: "Decline bench press with dumbbells",
    category: "Push",
    primaryMuscles: ["Chest"],
    secondaryMuscles: ["Triceps", "Front Delts"]
  },

  // Pull Exercises
  {
    name: "Pull-ups",
    description: "Bodyweight exercise hanging from bar, palms away",
    category: "Pull",
    primaryMuscles: ["Lats", "Back"],
    secondaryMuscles: ["Biceps", "Rear Delts", "Core"]
  },
  {
    name: "Chin-ups",
    description: "Bodyweight exercise hanging from bar, palms toward you",
    category: "Pull",
    primaryMuscles: ["Lats", "Back"],
    secondaryMuscles: ["Biceps", "Rear Delts"]
  },
  {
    name: "Lat Pulldowns",
    description: "Cable exercise mimicking pull-up motion",
    category: "Pull",
    primaryMuscles: ["Lats"],
    secondaryMuscles: ["Biceps", "Rear Delts", "Rhomboids"]
  },
  {
    name: "Barbell Rows",
    description: "Bent-over rowing motion with barbell",
    category: "Pull",
    primaryMuscles: ["Lats", "Rhomboids"],
    secondaryMuscles: ["Biceps", "Rear Delts", "Traps"]
  },
  {
    name: "Dumbbell Rows",
    description: "Single-arm or bent-over dumbbell rowing",
    category: "Pull",
    primaryMuscles: ["Lats", "Rhomboids"],
    secondaryMuscles: ["Biceps", "Rear Delts", "Traps"]
  },
  {
    name: "Single-Arm Dumbbell Row",
    description: "One-arm dumbbell row with knee on bench",
    category: "Pull",
    primaryMuscles: ["Lats", "Rhomboids"],
    secondaryMuscles: ["Biceps", "Rear Delts", "Traps"]
  },
  {
    name: "T-Bar Rows",
    description: "Rowing exercise using T-bar or landmine setup",
    category: "Pull",
    primaryMuscles: ["Lats", "Rhomboids"],
    secondaryMuscles: ["Biceps", "Rear Delts", "Traps"]
  },
  {
    name: "Cable Rows",
    description: "Seated cable rowing exercise",
    category: "Pull",
    primaryMuscles: ["Lats", "Rhomboids"],
    secondaryMuscles: ["Biceps", "Rear Delts", "Traps"]
  },
  {
    name: "Face Pulls",
    description: "Cable exercise for rear delts and upper back",
    category: "Pull",
    primaryMuscles: ["Rear Delts"],
    secondaryMuscles: ["Rhomboids", "Traps"]
  },
  {
    name: "Reverse Flyes",
    description: "Dumbbell exercise for rear deltoids",
    category: "Pull",
    primaryMuscles: ["Rear Delts"],
    secondaryMuscles: ["Rhomboids", "Traps"]
  },
  {
    name: "Shrugs",
    description: "Shoulder shrugs for trapezius muscles",
    category: "Pull",
    primaryMuscles: ["Traps"],
    secondaryMuscles: ["Rhomboids"]
  },
  {
    name: "Upright Rows",
    description: "Vertical pulling motion to chin level",
    category: "Pull",
    primaryMuscles: ["Traps", "Side Delts"],
    secondaryMuscles: ["Biceps", "Rear Delts"]
  },
  {
    name: "Bicep Curls",
    description: "Basic bicep isolation with dumbbells or barbell",
    category: "Pull",
    primaryMuscles: ["Biceps"],
    secondaryMuscles: ["Forearms"]
  },
  {
    name: "Hammer Curls",
    description: "Neutral grip bicep curls",
    category: "Pull",
    primaryMuscles: ["Biceps"],
    secondaryMuscles: ["Forearms"]
  },
  {
    name: "Preacher Curls",
    description: "Bicep curls on preacher bench",
    category: "Pull",
    primaryMuscles: ["Biceps"],
    secondaryMuscles: ["Forearms"]
  },
  {
    name: "Cable Curls",
    description: "Bicep curls using cable machine",
    category: "Pull",
    primaryMuscles: ["Biceps"],
    secondaryMuscles: ["Forearms"]
  },

  // Legs Exercises
  // Legs Exercises
  {
    name: "Squats",
    description: "Fundamental leg exercise, barbell on shoulders",
    category: "Legs",
    primaryMuscles: ["Quads", "Hamstrings", "Glutes"],
    secondaryMuscles: ["Core", "Back", "Calves"]
  },
  {
    name: "Front Squats",
    description: "Squats with barbell held in front rack position",
    category: "Legs",
    primaryMuscles: ["Quads", "Glutes"],
    secondaryMuscles: ["Hamstrings", "Core"]
  },
  {
    name: "Goblet Squats",
    description: "Squats holding dumbbell or kettlebell at chest",
    category: "Legs",
    primaryMuscles: ["Quads", "Glutes"],
    secondaryMuscles: ["Hamstrings", "Core"]
  },
  {
    name: "Bulgarian Split Squats",
    description: "Single-leg squats with rear foot elevated",
    category: "Legs",
    primaryMuscles: ["Quads", "Glutes"],
    secondaryMuscles: ["Hamstrings", "Core"]
  },
  {
    name: "Lunges",
    description: "Forward stepping lunge movement",
    category: "Legs",
    primaryMuscles: ["Quads", "Glutes"],
    secondaryMuscles: ["Hamstrings", "Core"]
  },
  {
    name: "Reverse Lunges",
    description: "Backward stepping lunge movement",
    category: "Legs",
    primaryMuscles: ["Quads", "Glutes"],
    secondaryMuscles: ["Hamstrings", "Core"]
  },
  {
    name: "Walking Lunges",
    description: "Continuous forward lunging motion",
    category: "Legs",
    primaryMuscles: ["Quads", "Glutes"],
    secondaryMuscles: ["Hamstrings", "Core"]
  },
  {
    name: "Dumbbell Lunges",
    description: "Lunges holding dumbbells for added resistance",
    category: "Legs",
    primaryMuscles: ["Quads", "Glutes"],
    secondaryMuscles: ["Hamstrings", "Core"]
  },
  {
    name: "Deadlifts",
    description: "Hip hinge movement lifting barbell from floor",
    category: "Legs",
    primaryMuscles: ["Hamstrings", "Glutes"],
    secondaryMuscles: ["Quads", "Back", "Traps", "Core", "Forearms"]
  },
  {
    name: "Romanian Deadlifts",
    description: "Deadlift variant focusing on hamstrings",
    category: "Legs",
    primaryMuscles: ["Hamstrings", "Glutes"],
    secondaryMuscles: ["Back", "Core"]
  },
  {
    name: "Dumbbell Romanian Deadlifts",
    description: "Romanian deadlifts using dumbbells",
    category: "Legs",
    primaryMuscles: ["Hamstrings", "Glutes"],
    secondaryMuscles: ["Back", "Core"]
  },
  {
    name: "Sumo Deadlifts",
    description: "Wide stance deadlift variation",
    category: "Legs",
    primaryMuscles: ["Hamstrings", "Glutes"],
    secondaryMuscles: ["Quads", "Back", "Traps", "Core"]
  },
  {
    name: "Leg Press",
    description: "Machine-based leg pushing exercise",
    category: "Legs",
    primaryMuscles: ["Quads", "Glutes"],
    secondaryMuscles: ["Hamstrings"]
  },
  {
    name: "Leg Curls",
    description: "Hamstring isolation on leg curl machine",
    category: "Legs",
    primaryMuscles: ["Hamstrings"],
    secondaryMuscles: []
  },
  {
    name: "Leg Extensions",
    description: "Quadricep isolation on extension machine",
    category: "Legs",
    primaryMuscles: ["Quads"],
    secondaryMuscles: []
  },
  {
    name: "Calf Raises",
    description: "Standing or seated calf muscle exercise",
    category: "Legs",
    primaryMuscles: ["Calves"],
    secondaryMuscles: []
  },
  {
    name: "Hip Thrusts",
    description: "Hip extension exercise for glutes",
    category: "Legs",
    primaryMuscles: ["Glutes"],
    secondaryMuscles: ["Hamstrings", "Core"]
  },
  {
    name: "Glute Bridges",
    description: "Floor-based glute activation exercise",
    category: "Legs",
    primaryMuscles: ["Glutes"],
    secondaryMuscles: ["Hamstrings", "Core"]
  },
  {
    name: "Step-ups",
    description: "Stepping up onto bench or platform",
    category: "Legs",
    primaryMuscles: ["Quads", "Glutes"],
    secondaryMuscles: ["Hamstrings", "Core"]
  },
  // ——— additional useful leg drills ———
  {
    name: "Hack Squats",
    description: "Machine-based squat with fixed bar path",
    category: "Legs",
    primaryMuscles: ["Quads"],
    secondaryMuscles: ["Glutes", "Hamstrings", "Core"]
  },
  {
    name: "Sissy Squat",
    description: "Knee-dominant squat isolating quads",
    category: "Legs",
    primaryMuscles: ["Quads"],
    secondaryMuscles: []
  },
  {
    name: "Glute-Ham Raise",
    description: "Back-side training on GHR bench",
    category: "Legs",
    primaryMuscles: ["Hamstrings"],
    secondaryMuscles: ["Glutes"]
  },
  {
    name: "Nordic Ham Curl",
    description: "Eccentric-only hamstring curl technique",
    category: "Legs",
    primaryMuscles: ["Hamstrings"],
    secondaryMuscles: []
  },
  {
    name: "Good Morning",
    description: "Hip-hinge movement stressing posterior chain",
    category: "Legs",
    primaryMuscles: ["Hamstrings", "Glutes"],
    secondaryMuscles: ["Back", "Core"]
  },
  {
    name: "Trap-Bar Deadlift",
    description: "Deadlift variant using a trap bar for balanced loading",
    category: "Legs",
    primaryMuscles: ["Quads", "Hamstrings", "Glutes"],
    secondaryMuscles: ["Back", "Core", "Traps", "Forearms"]
  },
  {
    name: "Glute Kickback Machine",
    description: "Machine or cable-pulley hip extension targeting glutes",
    category: "Legs",
    primaryMuscles: ["Glutes"],
    secondaryMuscles: ["Hamstrings"]
  },
  {
    name: "Hip Abductor Machine",
    description: "Machine-based hip abduction for outer thigh",
    category: "Legs",
    primaryMuscles: ["Glutes"],
    secondaryMuscles: []
  },
  {
    name: "Hip Adductor Machine",
    description: "Machine-based hip adduction for inner thigh",
    category: "Legs",
    primaryMuscles: ["Glutes"],
    secondaryMuscles: []
  },
  {
    name: "Seated Calf Raise",
    description: "Bench or lever-machine calf raise focusing on bent-knee plantarflexion",
    category: "Legs",
    primaryMuscles: ["Calves"],
    secondaryMuscles: []
  },
  {
    name: "Standing Calf Raise",
    description: "Machine or platform calf raise with straight-knee plantarflexion",
    category: "Legs",
    primaryMuscles: ["Calves"],
    secondaryMuscles: []
  },
  {
    name: "Lying Leg Curl",
    description: "Prone machine-based hamstring curl",
    category: "Legs",
    primaryMuscles: ["Hamstrings"],
    secondaryMuscles: []
  },

  // Core Exercises
  {
    name: "Planks",
    description: "Isometric core hold in push-up position",
    category: "Core",
    primaryMuscles: ["Core"],
    secondaryMuscles: ["Shoulders"]
  },
  {
    name: "Side Planks",
    description: "Lateral core hold on one side",
    category: "Core",
    primaryMuscles: ["Obliques"],
    secondaryMuscles: ["Core", "Shoulders"]
  },
  {
    name: "Crunches",
    description: "Basic abdominal flexion exercise",
    category: "Core",
    primaryMuscles: ["Abs"],
    secondaryMuscles: []
  },
  {
    name: "Bicycle Crunches",
    description: "Alternating knee-to-elbow crunches",
    category: "Core",
    primaryMuscles: ["Abs"],
    secondaryMuscles: ["Obliques"]
  },
  {
    name: "Russian Twists",
    description: "Seated rotational core exercise",
    category: "Core",
    primaryMuscles: ["Obliques"],
    secondaryMuscles: ["Abs"]
  },
  {
    name: "Dead Bugs",
    description: "Core stability exercise lying on back",
    category: "Core",
    primaryMuscles: ["Core"],
    secondaryMuscles: []
  },
  {
    name: "Bird Dogs",
    description: "Quadruped core stability exercise",
    category: "Core",
    primaryMuscles: ["Core"],
    secondaryMuscles: ["Glutes", "Back"]
  },
  {
    name: "Mountain Climbers",
    description: "Dynamic core exercise in plank position",
    category: "Core",
    primaryMuscles: ["Core"],
    secondaryMuscles: ["Shoulders", "Cardiovascular"]
  },
  {
    name: "Leg Raises",
    description: "Lower abdominal exercise lifting legs",
    category: "Core",
    primaryMuscles: ["Abs"],
    secondaryMuscles: ["Core"]
  },
  {
    name: "Hanging Leg Raises",
    description: "Advanced core exercise hanging from bar",
    category: "Core",
    primaryMuscles: ["Abs"],
    secondaryMuscles: ["Core", "Forearms"]
  },
  {
    name: "Ab Wheel Rollouts",
    description: "Core exercise using ab wheel for extension",
    category: "Core",
    primaryMuscles: ["Core"],
    secondaryMuscles: ["Shoulders", "Back"]
  },
  {
    name: "Wood Chops",
    description: "Rotational core exercise with weight",
    category: "Core",
    primaryMuscles: ["Obliques"],
    secondaryMuscles: ["Core", "Shoulders"]
  },
  {
    name: "Hanging Knee Raises",
    description: "Hanging from bar, bring knees to chest",
    category: "Core",
    primaryMuscles: ["Abs"],
    secondaryMuscles: ["Core", "Forearms"]
  },
  {
    name: "Pallof Press",
    description: "Anti-rotation core exercise with cable",
    category: "Core",
    primaryMuscles: ["Core"],
    secondaryMuscles: ["Obliques", "Shoulders"]
  },

  // Cardio Exercises
  {
    name: "Treadmill Running",
    description: "Running or jogging on treadmill machine",
    category: "Cardio",
    primaryMuscles: ["Cardiovascular"],
    secondaryMuscles: ["Calves", "Quads"]
  },
  {
    name: "Elliptical",
    description: "Low-impact cardio on elliptical machine",
    category: "Cardio",
    primaryMuscles: ["Cardiovascular"],
    secondaryMuscles: ["Quads", "Hamstrings"]
  },
  {
    name: "Stationary Bike",
    description: "Cycling on stationary exercise bike",
    category: "Cardio",
    primaryMuscles: ["Cardiovascular"],
    secondaryMuscles: ["Quads", "Calves"]
  },
  {
    name: "Rowing Machine",
    description: "Full-body cardio on rowing ergometer",
    category: "Cardio",
    primaryMuscles: ["Cardiovascular"],
    secondaryMuscles: ["Back", "Quads", "Hamstrings", "Core"]
  },
  {
    name: "Stair Climber",
    description: "Cardio exercise on stair climbing machine",
    category: "Cardio",
    primaryMuscles: ["Cardiovascular"],
    secondaryMuscles: ["Quads", "Calves", "Glutes"]
  },
  {
    name: "Jump Rope",
    description: "Cardio exercise jumping over rope",
    category: "Cardio",
    primaryMuscles: ["Cardiovascular"],
    secondaryMuscles: ["Calves", "Forearms"]
  },
  {
    name: "Burpees",
    description: "Full-body cardio exercise with squat, plank, jump",
    category: "Cardio",
    primaryMuscles: ["Full Body"],
    secondaryMuscles: ["Cardiovascular"]
  },
  {
    name: "High Knees",
    description: "Running in place with high knee lift",
    category: "Cardio",
    primaryMuscles: ["Cardiovascular"],
    secondaryMuscles: ["Core", "Quads"]
  },
  {
    name: "Jumping Jacks",
    description: "Cardio exercise with jumping arm and leg movements",
    category: "Cardio",
    primaryMuscles: ["Cardiovascular"],
    secondaryMuscles: ["Calves", "Shoulders"]
  },

  // Functional/Other Exercises
  {
    name: "Turkish Get-ups",
    description: "Complex full-body movement from lying to standing",
    category: "Functional",
    primaryMuscles: ["Full Body"],
    secondaryMuscles: ["Core", "Shoulders"]
  },
  {
    name: "Farmer's Walks",
    description: "Walking while carrying heavy weights",
    category: "Functional",
    primaryMuscles: ["Forearms", "Traps"],
    secondaryMuscles: ["Core", "Calves"]
  },
  {
    name: "Sled Push",
    description: "Pushing weighted sled across floor",
    category: "Functional",
    primaryMuscles: ["Quads"],
    secondaryMuscles: ["Glutes", "Core", "Cardiovascular"]
  },
  {
    name: "Sled Pull",
    description: "Pulling weighted sled with rope or straps",
    category: "Functional",
    primaryMuscles: ["Back"],
    secondaryMuscles: ["Biceps", "Core", "Cardiovascular"]
  },
  {
    name: "Kettlebell Swings",
    description: "Hip hinge movement swinging kettlebell",
    category: "Functional",
    primaryMuscles: ["Glutes", "Hamstrings"],
    secondaryMuscles: ["Core", "Shoulders", "Cardiovascular"]
  },
  {
    name: "Kettlebell Goblet Squats",
    description: "Squats holding kettlebell at chest",
    category: "Functional",
    primaryMuscles: ["Quads"],
    secondaryMuscles: ["Glutes", "Core"]
  },
  {
    name: "Medicine Ball Slams",
    description: "Overhead slamming of medicine ball",
    category: "Functional",
    primaryMuscles: ["Core"],
    secondaryMuscles: ["Shoulders", "Cardiovascular"]
  },
  {
    name: "Box Jumps",
    description: "Jumping onto elevated box or platform",
    category: "Functional",
    primaryMuscles: ["Quads"],
    secondaryMuscles: ["Glutes", "Calves", "Cardiovascular"]
  },
  {
    name: "Battle Ropes",
    description: "Wave-making exercise with heavy ropes",
    category: "Functional",
    primaryMuscles: ["Shoulders"],
    secondaryMuscles: ["Core", "Cardiovascular"]
  },
  {
    name: "Tire Flips",
    description: "Flipping large tire end over end",
    category: "Functional",
    primaryMuscles: ["Full Body"],
    secondaryMuscles: ["Cardiovascular"]
  },

  // Machine Exercises
  {
    name: "Leg Press 45-Degree",
    description: "45-degree angled leg press machine",
    category: "Machine",
    primaryMuscles: ["Quads"],
    secondaryMuscles: ["Glutes", "Hamstrings"]
  },
  {
    name: "Leg Press Horizontal",
    description: "Horizontal leg press machine",
    category: "Machine",
    primaryMuscles: ["Quads"],
    secondaryMuscles: ["Glutes", "Hamstrings"]
  },
  {
    name: "Seated Leg Curls",
    description: "Hamstring curls on seated machine",
    category: "Machine",
    primaryMuscles: ["Hamstrings"],
    secondaryMuscles: ["Glutes"]
  },
  {
    name: "Lying Leg Curls",
    description: "Hamstring curls lying face down",
    category: "Machine",
    primaryMuscles: ["Hamstrings"],
    secondaryMuscles: ["Glutes"]
  },
  {
    name: "Standing Leg Curls",
    description: "Single-leg hamstring curls standing",
    category: "Machine",
    primaryMuscles: ["Hamstrings"],
    secondaryMuscles: ["Glutes"]
  },
  {
    name: "Leg Extension Machine",
    description: "Quadricep isolation on leg extension machine",
    category: "Machine",
    primaryMuscles: ["Quads"],
    secondaryMuscles: []
  },
  {
    name: "Standing Calf Raise Machine",
    description: "Calf raises on standing machine with weight",
    category: "Machine",
    primaryMuscles: ["Calves"],
    secondaryMuscles: []
  },
  {
    name: "Seated Calf Raise Machine",
    description: "Calf raises on seated machine",
    category: "Machine",
    primaryMuscles: ["Calves"],
    secondaryMuscles: []
  },
  {
    name: "Hip Abductor Machine",
    description: "Seated machine for outer thigh/glutes",
    category: "Machine",
    primaryMuscles: ["Glutes"],
    secondaryMuscles: []
  },
  {
    name: "Hip Adductor Machine", 
    description: "Seated machine for inner thigh",
    category: "Machine",
    primaryMuscles: ["Glutes"],
    secondaryMuscles: []
  },

  // Smith Machine Exercises
  {
    name: "Smith Machine Squats",
    description: "Squats using Smith machine for guided bar path",
    category: "Legs",
    primaryMuscles: ["Quads"],
    secondaryMuscles: ["Glutes", "Hamstrings"]
  },
  {
    name: "Smith Machine Front Squats",
    description: "Front squats using Smith machine",
    category: "Legs",
    primaryMuscles: ["Quads"],
    secondaryMuscles: ["Glutes", "Core"]
  },
  {
    name: "Smith Machine Split Squats",
    description: "Bulgarian split squats using Smith machine",
    category: "Legs",
    primaryMuscles: ["Quads"],
    secondaryMuscles: ["Glutes", "Hamstrings"]
  },
  {
    name: "Smith Machine Lunges",
    description: "Static lunges using Smith machine bar",
    category: "Legs",
    primaryMuscles: ["Quads"],
    secondaryMuscles: ["Glutes", "Hamstrings"]
  },
  {
    name: "Smith Machine Romanian Deadlifts",
    description: "RDLs using Smith machine for controlled movement",
    category: "Legs",
    primaryMuscles: ["Hamstrings"],
    secondaryMuscles: ["Glutes", "Back"]
  },
  {
    name: "Smith Machine Deadlifts",
    description: "Conventional deadlifts using Smith machine",
    category: "Legs",
    primaryMuscles: ["Hamstrings", "Glutes"],
    secondaryMuscles: ["Back", "Traps"]
  },
  {
    name: "Smith Machine Stiff Leg Deadlifts",
    description: "Straight leg deadlifts on Smith machine",
    category: "Legs",
    primaryMuscles: ["Hamstrings"],
    secondaryMuscles: ["Glutes", "Back"]
  },
  {
    name: "Smith Machine Bench Press",
    description: "Bench press using Smith machine for safety",
    category: "Push",
    primaryMuscles: ["Chest"],
    secondaryMuscles: ["Triceps", "Front Delts"]
  },
  {
    name: "Smith Machine Incline Press",
    description: "Incline bench press on Smith machine",
    category: "Push",
    primaryMuscles: ["Chest"],
    secondaryMuscles: ["Front Delts", "Triceps"]
  },
  {
    name: "Smith Machine Decline Press",
    description: "Decline bench press on Smith machine", 
    category: "Push",
    primaryMuscles: ["Chest"],
    secondaryMuscles: ["Triceps", "Front Delts"]
  },
  {
    name: "Smith Machine Overhead Press",
    description: "Standing overhead press using Smith machine",
    category: "Push",
    primaryMuscles: ["Shoulders"],
    secondaryMuscles: ["Triceps", "Core"]
  },
  {
    name: "Smith Machine Behind Neck Press",
    description: "Overhead press behind neck on Smith machine",
    category: "Push",
    primaryMuscles: ["Shoulders"],
    secondaryMuscles: ["Triceps"]
  },
  {
    name: "Smith Machine Rows",
    description: "Bent-over rows using Smith machine bar",
    category: "Pull",
    primaryMuscles: ["Lats", "Rhomboids"],
    secondaryMuscles: ["Biceps", "Rear Delts"]
  },
  {
    name: "Smith Machine Inverted Rows",
    description: "Bodyweight rows under Smith machine bar",
    category: "Pull",
    primaryMuscles: ["Lats", "Rhomboids"],
    secondaryMuscles: ["Biceps", "Rear Delts"]
  },
  {
    name: "Smith Machine Upright Rows",
    description: "Upright rows using Smith machine bar",
    category: "Pull",
    primaryMuscles: ["Traps", "Side Delts"],
    secondaryMuscles: ["Biceps"]
  },
  {
    name: "Smith Machine Shrugs",
    description: "Shoulder shrugs using Smith machine",
    category: "Pull",
    primaryMuscles: ["Traps"],
    secondaryMuscles: ["Rhomboids"]
  },
  {
    name: "Smith Machine Calf Raises",
    description: "Standing calf raises with Smith machine bar",
    category: "Legs",
    primaryMuscles: ["Calves"],
    secondaryMuscles: []
  },
  {
    name: "Smith Machine Hip Thrusts",
    description: "Hip thrusts using Smith machine bar",
    category: "Legs",
    primaryMuscles: ["Glutes"],
    secondaryMuscles: ["Hamstrings", "Core"]
  },
  {
    name: "Smith Machine Good Mornings",
    description: "Hip hinge movement with Smith machine bar",
    category: "Legs",
    primaryMuscles: ["Hamstrings"],
    secondaryMuscles: ["Glutes", "Back", "Core"]
  },

  // Cable Machine Exercises
  {
    name: "Cable Lateral Raises",
    description: "Side raises using cable machine",
    category: "Push",
    primaryMuscles: ["Side Delts"],
    secondaryMuscles: []
  },
  {
    name: "Cable Rear Delt Flyes",
    description: "Rear deltoid isolation with cables",
    category: "Pull",
    primaryMuscles: ["Rear Delts"],
    secondaryMuscles: ["Rhomboids"]
  },
  {
    name: "Cable Chest Flyes",
    description: "Chest isolation using cable crossover",
    category: "Push",
    primaryMuscles: ["Chest"],
    secondaryMuscles: ["Front Delts"]
  },
  {
    name: "Cable Crossovers",
    description: "High to low cable chest exercise",
    category: "Push",
    primaryMuscles: ["Chest"],
    secondaryMuscles: ["Front Delts"]
  },
  {
    name: "Cable Tricep Pushdowns",
    description: "Tricep isolation using cable machine",
    category: "Push",
    primaryMuscles: ["Triceps"],
    secondaryMuscles: []
  },
  {
    name: "Cable Overhead Tricep Extension",
    description: "Overhead tricep extension with rope attachment",
    category: "Push",
    primaryMuscles: ["Triceps"],
    secondaryMuscles: ["Core"]
  },
  {
    name: "Cable Hammer Curls",
    description: "Hammer curls using cable with rope",
    category: "Pull",
    primaryMuscles: ["Biceps"],
    secondaryMuscles: ["Forearms"]
  },
  {
    name: "Cable Preacher Curls",
    description: "Preacher curls using cable machine",
    category: "Pull",
    primaryMuscles: ["Biceps"],
    secondaryMuscles: ["Forearms"]
  },
  {
    name: "Cable Wood Chops High to Low",
    description: "Rotational core exercise, high to low",
    category: "Core",
    primaryMuscles: ["Obliques"],
    secondaryMuscles: ["Core", "Shoulders"]
  },
  {
    name: "Cable Wood Chops Low to High",
    description: "Rotational core exercise, low to high", 
    category: "Core",
    primaryMuscles: ["Obliques"],
    secondaryMuscles: ["Core", "Shoulders"]
  },
  {
    name: "Cable Crunches",
    description: "Kneeling cable crunches for abs",
    category: "Core",
    primaryMuscles: ["Abs"],
    secondaryMuscles: ["Core"]
  },
  {
    name: "Cable Side Bends",
    description: "Lateral core flexion with cable",
    category: "Core",
    primaryMuscles: ["Obliques"],
    secondaryMuscles: ["Core"]
  },
  {
    name: "Cable Front Raises",
    description: "Front raises using cable machine",
    category: "Push",
    primaryMuscles: ["Front Delts"],
    secondaryMuscles: ["Core"]
  },
  {
    name: "Cable Shoulder Press",
    description: "Shoulder press using cable machine",
    category: "Push",
    primaryMuscles: ["Shoulders"],
    secondaryMuscles: ["Triceps", "Core"]
  },
  {
    name: "Cable Shrugs",
    description: "Shoulder shrugs using cable machine",
    category: "Pull",
    primaryMuscles: ["Traps"],
    secondaryMuscles: ["Rhomboids"]
  },

  // Additional Machine Exercises
  {
    name: "Chest Press Machine",
    description: "Seated chest press on machine",
    category: "Machine",
    primaryMuscles: ["Chest"],
    secondaryMuscles: ["Triceps", "Front Delts"]
  },
  {
    name: "Incline Chest Press Machine",
    description: "Incline chest press on machine",
    category: "Machine",
    primaryMuscles: ["Chest"],
    secondaryMuscles: ["Front Delts", "Triceps"]
  },
  {
    name: "Shoulder Press Machine",
    description: "Seated shoulder press machine",
    category: "Machine",
    primaryMuscles: ["Shoulders"],
    secondaryMuscles: ["Triceps"]
  },
  {
    name: "Lateral Raise Machine",
    description: "Lateral raises on machine",
    category: "Machine",
    primaryMuscles: ["Side Delts"],
    secondaryMuscles: []
  },
  {
    name: "Rear Delt Machine",
    description: "Reverse fly machine for rear delts",
    category: "Machine",
    primaryMuscles: ["Rear Delts"],
    secondaryMuscles: ["Rhomboids"]
  },
  {
    name: "Lat Pulldown Wide Grip",
    description: "Wide grip lat pulldown",
    category: "Machine",
    primaryMuscles: ["Lats"],
    secondaryMuscles: ["Biceps", "Rear Delts", "Rhomboids"]
  },
  {
    name: "Lat Pulldown Close Grip",
    description: "Close grip lat pulldown",
    category: "Machine",
    primaryMuscles: ["Lats"],
    secondaryMuscles: ["Biceps", "Rhomboids"]
  },
  {
    name: "Seated Cable Row Wide Grip",
    description: "Wide grip seated cable row",
    category: "Machine",
    primaryMuscles: ["Lats", "Rhomboids"],
    secondaryMuscles: ["Biceps", "Rear Delts", "Traps"]
  },
  {
    name: "Seated Cable Row Close Grip",
    description: "Close grip seated cable row",
    category: "Machine",
    primaryMuscles: ["Lats", "Rhomboids"],
    secondaryMuscles: ["Biceps", "Rear Delts"]
  },
  {
    name: "Machine Preacher Curls",
    description: "Preacher curls on machine",
    category: "Machine",
    primaryMuscles: ["Biceps"],
    secondaryMuscles: ["Forearms"]
  },
  {
    name: "Machine Tricep Dips",
    description: "Assisted or weighted dip machine",
    category: "Machine",
    primaryMuscles: ["Triceps"],
    secondaryMuscles: ["Chest", "Front Delts"]
  },
  {
    name: "Assisted Pull-up Machine",
    description: "Pull-ups with machine assistance",
    category: "Machine",
    primaryMuscles: ["Lats", "Back"],
    secondaryMuscles: ["Biceps", "Rear Delts"]
  },
  {
    name: "Assisted Chin-up Machine",
    description: "Chin-ups with machine assistance",
    category: "Machine",
    primaryMuscles: ["Lats", "Back"],
    secondaryMuscles: ["Biceps", "Rear Delts"]
  }
];

// Export library with deterministic IDs for consistent sharing
export const exerciseLibrary: Omit<Exercise, 'createdAt' | 'updatedAt'>[] = 
  exerciseLibraryRaw.map(exercise => ({
    ...exercise,
    id: generateExerciseId(exercise.name)
  }));